/**
 * Offline Storage Service
 * Handles localStorage for form data and IndexedDB for audio blobs.
 */

const DB_NAME = 'NBAEvalOffline';
const DB_VERSION = 1;
const AUDIO_STORE = 'audioRecordings';
const DRAFT_KEY_PREFIX = 'eval_draft_';
const SYNC_QUEUE_KEY = 'eval_sync_queue';
const REF_DATA_KEY_PREFIX = 'eval_ref_';
const LOCAL_PROSPECT_UID_PREFIX = 'local_prospect_';

/**
 * Validate that a UID is in a safe format (alphanumeric, hyphens, underscores only).
 * Prevents injection attacks through localStorage keys and data structures.
 * @param {string} uid - The UID to validate
 * @returns {boolean}
 */
export function isValidUID(uid) {
  if (typeof uid !== 'string' || !uid.trim()) {
    return false;
  }
  // Allow alphanumeric characters, hyphens, underscores, and dots
  // This covers typical UUID and numeric ID formats
  return /^[a-zA-Z0-9\-_.]+$/.test(uid);
}

/**
 * Sanitize a UID by removing or replacing invalid characters.
 * If the UID fails validation entirely, returns null.
 * @param {string} uid - The UID to sanitize
 * @returns {string|null}
 */
export function sanitizeUID(uid) {
  if (!uid || typeof uid !== 'string') {
    return null;
  }
  const trimmed = uid.trim();
  if (!trimmed) {
    return null;
  }
  // Remove any characters that are not alphanumeric, hyphens, underscores, or dots
  const sanitized = trimmed.replace(/[^a-zA-Z0-9\-_.]/g, '');
  return sanitized.length > 0 ? sanitized : null;
}

// ─── IndexedDB (for audio blobs) ────────────────────────────────────

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(AUDIO_STORE)) {
        const store = db.createObjectStore(AUDIO_STORE, { keyPath: 'id', autoIncrement: true });
        store.createIndex('draftKey', 'draftKey', { unique: false });
        store.createIndex('fieldName', 'fieldName', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Save an audio recording blob to IndexedDB.
 * @param {string} draftKey - Draft identifier
 * @param {string} fieldName - Field this recording belongs to (e.g., 'category_1', 'evaluationSummary')
 * @param {Blob} audioBlob - The recorded audio blob
 * @param {number} duration - Duration in seconds
 * @returns {Promise<number>} The auto-generated record ID
 */
export async function saveAudioRecording(draftKey, fieldName, audioBlob, duration) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(AUDIO_STORE, 'readwrite');
    const store = tx.objectStore(AUDIO_STORE);
    const record = {
      draftKey,
      fieldName,
      audioBlob,
      mimeType: audioBlob.type || 'audio/webm;codecs=opus',
      duration,
      timestamp: Date.now(),
      transcribed: false,
      transcription: null,
    };
    const request = store.add(record);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all audio recordings for a draft.
 * @param {string} draftKey
 * @returns {Promise<Array>}
 */
export async function getAudioRecordingsForDraft(draftKey) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(AUDIO_STORE, 'readonly');
    const store = tx.objectStore(AUDIO_STORE);
    const index = store.index('draftKey');
    const request = index.getAll(draftKey);
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get audio recordings for a specific field in a draft.
 * @param {string} draftKey
 * @param {string} fieldName
 * @returns {Promise<Array>}
 */
export async function getAudioRecordingsForField(draftKey, fieldName) {
  const recordings = await getAudioRecordingsForDraft(draftKey);
  return recordings.filter((r) => r.fieldName === fieldName);
}

/**
 * Delete a specific audio recording by ID.
 * @param {number} recordId
 */
export async function deleteAudioRecording(recordId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(AUDIO_STORE, 'readwrite');
    const store = tx.objectStore(AUDIO_STORE);
    const request = store.delete(recordId);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete all audio recordings for a draft.
 * @param {string} draftKey
 */
export async function deleteAudioRecordingsForDraft(draftKey) {
  const recordings = await getAudioRecordingsForDraft(draftKey);
  const db = await openDB();
  const tx = db.transaction(AUDIO_STORE, 'readwrite');
  const store = tx.objectStore(AUDIO_STORE);
  for (const rec of recordings) {
    store.delete(rec.id);
  }
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Update a recording with its transcription text.
 * @param {number} recordId
 * @param {string} transcription
 */
export async function updateRecordingTranscription(recordId, transcription) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(AUDIO_STORE, 'readwrite');
    const store = tx.objectStore(AUDIO_STORE);
    const getReq = store.get(recordId);
    getReq.onsuccess = () => {
      const record = getReq.result;
      if (record) {
        record.transcribed = true;
        record.transcription = transcription;
        const putReq = store.put(record);
        putReq.onsuccess = () => resolve();
        putReq.onerror = () => reject(putReq.error);
      } else {
        resolve();
      }
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

/**
 * Get all untranscribed recordings across all drafts.
 * @returns {Promise<Array>}
 */
export async function getUntranscribedRecordings() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(AUDIO_STORE, 'readonly');
    const store = tx.objectStore(AUDIO_STORE);
    const request = store.getAll();
    request.onsuccess = () => {
      resolve((request.result || []).filter((r) => !r.transcribed));
    };
    request.onerror = () => reject(request.error);
  });
}

export async function updateAudioRecordingsDraftKey(oldDraftKey, newDraftKey) {
  if (!oldDraftKey || !newDraftKey || oldDraftKey === newDraftKey) {
    return;
  }

  const recordings = await getAudioRecordingsForDraft(oldDraftKey);
  if (recordings.length === 0) {
    return;
  }

  const db = await openDB();
  const tx = db.transaction(AUDIO_STORE, 'readwrite');
  const store = tx.objectStore(AUDIO_STORE);
  recordings.forEach((record) => {
    store.put({
      ...record,
      draftKey: newDraftKey,
    });
  });

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ─── localStorage: Draft Form Data ──────────────────────────────────

/**
 * Generate a local placeholder UID for a prospect created offline.
 * @returns {string}
 */
export function createLocalProspectUID() {
  return `${LOCAL_PROSPECT_UID_PREFIX}${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

export function isLocalProspectUID(prospectUID) {
  return typeof prospectUID === 'string' && prospectUID.startsWith(LOCAL_PROSPECT_UID_PREFIX);
}

/**
 * Generate a draft key for a specific evaluation.
 * Validates input to prevent injection of malicious characters into localStorage keys.
 * @param {string} prospectUID
 * @param {number|string} eventId
 * @returns {string}
 * @throws {Error} If prospectUID fails security validation
 */
export function getDraftKey(prospectUID, eventId) {
  // Validate prospectUID to prevent injection attacks
  if (!isValidUID(prospectUID)) {
    // If invalid but possibly salvageable, attempt sanitization
    if (prospectUID) {
      const sanitized = sanitizeUID(prospectUID);
      if (!sanitized) {
        console.warn('Invalid prospectUID provided to getDraftKey:', { prospectUID });
        return `${DRAFT_KEY_PREFIX}new_${Date.now()}`;
      }
      prospectUID = sanitized;
    } else {
      return `${DRAFT_KEY_PREFIX}new_${Date.now()}`;
    }
  }

  // Validate eventId is a number or numeric string
  const eventIdNum = parseInt(eventId, 10);
  if (isNaN(eventIdNum) || eventIdNum <= 0) {
    return `${DRAFT_KEY_PREFIX}new_${Date.now()}`;
  }

  return `${DRAFT_KEY_PREFIX}${prospectUID}_${eventIdNum}`;
}

/**
 * Save evaluation draft to localStorage.
 * @param {string} draftKey
 * @param {Object} formData - The full form state
 */
export function saveDraft(draftKey, formData) {
  try {
    const draft = {
      ...formData,
      _draftKey: draftKey,
      _savedAt: Date.now(),
    };
    localStorage.setItem(draftKey, JSON.stringify(draft));
  } catch (e) {
    console.warn('Failed to save draft to localStorage:', e);
  }
}

/**
 * Sanitize a string read from an untrusted source (e.g., localStorage).
 * Strips script blocks and HTML tags to prevent XSS when values are
 * embedded in DOM output.
 * @param {string} str
 * @returns {string}
 */
function sanitizeDraftString(str) {
  return str
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<[^>]+>/g, '');
}

/**
 * Recursively sanitize all string fields of a value parsed from localStorage.
 * Non-string primitives and null are returned as-is; objects and arrays are
 * traversed so every nested string is sanitized.
 * @param {*} value
 * @returns {*}
 */
function sanitizeDraftValue(value) {
  if (typeof value === 'string') {
    return sanitizeDraftString(value);
  }
  if (Array.isArray(value)) {
    return value.map(sanitizeDraftValue);
  }
  if (value !== null && typeof value === 'object') {
    const result = {};
    for (const [k, v] of Object.entries(value)) {
      result[k] = sanitizeDraftValue(v);
    }
    return result;
  }
  return value;
}

/**
 * Validate sanitized draft shape to ensure untrusted payloads do not propagate
 * into the application state.
 * @param {*} draft
 * @returns {boolean}
 */
function isValidDraftPayload(draft) {
  if (!draft || typeof draft !== 'object' || Array.isArray(draft)) {
    return false;
  }

  if (draft._draftKey && (typeof draft._draftKey !== 'string' || !draft._draftKey.startsWith(DRAFT_KEY_PREFIX))) {
    return false;
  }

  if (draft.selectedProspectUID && !isValidUID(draft.selectedProspectUID)) {
    return false;
  }

  if (draft.selectedEvent?.id && (typeof draft.selectedEvent.id !== 'number' && typeof draft.selectedEvent.id !== 'string')) {
    return false;
  }

  if (draft.form && typeof draft.form !== 'object') {
    return false;
  }

  if (draft.selectedCompetencies && typeof draft.selectedCompetencies !== 'object') {
    return false;
  }

  if (draft.selectedTangibles && !Array.isArray(draft.selectedTangibles)) {
    return false;
  }

  return true;
}

/**
 * Load an evaluation draft from localStorage.
 * @param {string} draftKey
 * @returns {Object|null}
 */
export function loadDraft(draftKey) {
  try {
    if (!draftKey || typeof draftKey !== 'string' || !draftKey.startsWith(DRAFT_KEY_PREFIX)) {
      return null;
    }

    const data = localStorage.getItem(draftKey);
    if (!data) {
      return null;
    }

    const sanitizedDraft = sanitizeDraftValue(JSON.parse(data));
    return isValidDraftPayload(sanitizedDraft) ? sanitizedDraft : null;
  } catch {
    return null;
  }
}

/**
 * Delete a draft from localStorage.
 * @param {string} draftKey
 */
export function deleteDraft(draftKey) {
  try {
    localStorage.removeItem(draftKey);
  } catch { /* ignore */ }
}

/**
 * Get all saved drafts.
 * @returns {Array<Object>}
 */
export function getAllDrafts() {
  const drafts = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(DRAFT_KEY_PREFIX)) {
        const draft = loadDraft(key);
        if (draft) {
          drafts.push(draft);
        }
      }
    }
  } catch { /* ignore */ }
  return drafts;
}

export function replaceDraftProspectUID(oldProspectUID, newProspectUID) {
  if (!oldProspectUID || !newProspectUID || oldProspectUID === newProspectUID) {
    return [];
  }

  const updatedDraftKeys = [];
  const drafts = getAllDrafts();

  drafts.forEach((draft) => {
    if (draft.selectedProspectUID !== oldProspectUID) {
      return;
    }

    const oldDraftKey = draft._draftKey;
    const newDraftKey = getDraftKey(newProspectUID, draft.selectedEvent?.id);
    const updatedDraft = {
      ...draft,
      selectedProspectUID: newProspectUID,
      _draftKey: newDraftKey,
      _savedAt: Date.now(),
    };

    try {
      if (oldDraftKey && oldDraftKey !== newDraftKey) {
        localStorage.removeItem(oldDraftKey);
      }
      localStorage.setItem(newDraftKey, JSON.stringify(updatedDraft));
      updatedDraftKeys.push({ oldDraftKey, newDraftKey });
    } catch (error) {
      console.warn('Failed to replace draft prospect UID:', error);
    }
  });

  return updatedDraftKeys;
}

// ─── localStorage: Sync Queue ───────────────────────────────────────

/**
 * Get the sync queue (evaluations and prospects waiting to be submitted to server).
 * @returns {Array<Object>}
 */
export function getSyncQueue() {
  try {
    const data = localStorage.getItem(SYNC_QUEUE_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

function saveSyncQueue(queue) {
  try {
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
  } catch (error) {
    console.warn('Failed to persist sync queue:', error);
  }
}

function createQueueItem(baseItem) {
  return {
    id: `sync_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    addedAt: Date.now(),
    retryCount: 0,
    lastAttempt: null,
    status: 'pending',
    ...baseItem,
  };
}

function replaceOfficialUIDInEvaluationPayload(payload, newOfficialUID) {
  if (!payload) {
    return payload;
  }

  return {
    ...payload,
    officialUID: newOfficialUID,
    evaluationRatingList: (payload.evaluationRatingList || []).map((rating) => ({
      ...rating,
      officialUID: newOfficialUID,
    })),
    evaluationRatingCompetencyList: (payload.evaluationRatingCompetencyList || []).map((competency) => ({
      ...competency,
      officialUID: newOfficialUID,
    })),
  };
}

/**
 * Add an evaluation payload to the sync queue.
 * @param {Object} payload - The full API payload
 * @param {string} draftKey - Associated draft key for cleanup
 * @returns {string} Queue item ID
 */
export function addToSyncQueue(payload, draftKey) {
  const queue = getSyncQueue();
  const queueItem = createQueueItem({
    itemType: 'evaluation',
    payload,
    draftKey,
  });
  queue.push(queueItem);
  saveSyncQueue(queue);
  return queueItem.id;
}

export function addOrUpdateProspectSyncQueueItem(payload, localProspectUID) {
  const queue = getSyncQueue();
  const existingIndex = queue.findIndex((item) => item.itemType === 'prospect' && item.localProspectUID === localProspectUID);

  if (existingIndex >= 0) {
    queue[existingIndex] = {
      ...queue[existingIndex],
      payload,
      localProspectUID,
      lastAttempt: null,
      status: 'pending',
    };
    saveSyncQueue(queue);
    return queue[existingIndex].id;
  }

  const queueItem = createQueueItem({
    itemType: 'prospect',
    payload,
    localProspectUID,
  });
  queue.push(queueItem);
  saveSyncQueue(queue);
  return queueItem.id;
}

/**
 * Add or update a sync queue item.
 * For existing evaluations (evaluationID > 0), updates the existing entry.
 * For new evaluations (evaluationID = 0), uses eventID + officialUID + evaluationDate as a unique key.
 * @param {Object} payload - The full API payload
 * @param {string} draftKey - Associated draft key for cleanup
 * @returns {string} Queue item ID
 */
export function addOrUpdateSyncQueueItem(payload, draftKey) {
  const queue = getSyncQueue();
  const evaluationID = payload.evaluationID || 0;

  let existingIndex = -1;
  if (evaluationID > 0) {
    existingIndex = queue.findIndex((item) => item.itemType !== 'prospect' && item.payload?.evaluationID === evaluationID);
  } else {
    const { eventID, officialUID, evaluationDate } = payload;
    existingIndex = queue.findIndex((item) =>
      item.itemType !== 'prospect'
      && item.payload?.eventID === eventID
      && item.payload?.officialUID === officialUID
      && item.payload?.evaluationDate === evaluationDate
      && (!item.payload?.evaluationID || item.payload?.evaluationID === 0)
    );
  }

  if (existingIndex >= 0) {
    const updatedItem = {
      ...queue[existingIndex],
      itemType: 'evaluation',
      payload,
      draftKey,
      lastAttempt: null,
      status: 'pending',
    };
    queue[existingIndex] = updatedItem;
    saveSyncQueue(queue);
    return updatedItem.id;
  }

  const queueItem = createQueueItem({
    itemType: 'evaluation',
    payload,
    draftKey,
  });
  queue.push(queueItem);
  saveSyncQueue(queue);
  return queueItem.id;
}

export function replaceQueuedProspectReferences(oldProspectUID, newProspectUID, draftKeyChanges = []) {
  if (!oldProspectUID || !newProspectUID || oldProspectUID === newProspectUID) {
    return;
  }

  const draftKeyMap = new Map(draftKeyChanges.map(({ oldDraftKey, newDraftKey }) => [oldDraftKey, newDraftKey]));
  const queue = getSyncQueue().map((item) => {
    const nextItem = { ...item };

    if (draftKeyMap.has(nextItem.draftKey)) {
      nextItem.draftKey = draftKeyMap.get(nextItem.draftKey);
    }

    if (nextItem.itemType === 'evaluation' && nextItem.payload?.officialUID === oldProspectUID) {
      nextItem.payload = replaceOfficialUIDInEvaluationPayload(nextItem.payload, newProspectUID);
      if (!draftKeyMap.has(nextItem.draftKey) && nextItem.payload?.eventID) {
        nextItem.draftKey = getDraftKey(newProspectUID, nextItem.payload.eventID);
      }
    }

    return nextItem;
  });

  saveSyncQueue(queue);
}

export function updateQueuedEvaluationTextFromDraft(draftKey, draftData) {
  if (!draftKey || !draftData?.form) {
    return false;
  }

  const queue = getSyncQueue();
  const queueIndex = queue.findIndex((item) => item.itemType === 'evaluation' && item.draftKey === draftKey);
  if (queueIndex < 0) {
    return false;
  }

  const existingItem = queue[queueIndex];
  const existingPayload = existingItem.payload || {};
  const existingCommentsByCategory = new Map(
    (existingPayload.commentsList || []).map((comment) => [comment.categoryID, comment])
  );

  const commentsList = Object.keys(draftData.form)
    .filter((key) => /^category_\d+$/.test(key))
    .map((key) => {
      const categoryID = Number(key.split('_')[1]);
      const commentText = `${draftData.form[key] || ''}`.trim();
      if (!commentText) {
        return null;
      }

      const existingComment = existingCommentsByCategory.get(categoryID) || {};
      return {
        ...existingComment,
        commentId: existingComment.commentId || 0,
        evaluationID: existingComment.evaluationID || existingPayload.evaluationID || 0,
        categoryID,
        comment: commentText,
      };
    })
    .filter(Boolean);

  queue[queueIndex] = {
    ...existingItem,
    payload: {
      ...existingPayload,
      refereeName: draftData.form.refereeName || existingPayload.refereeName || '',
      comments: draftData.form.evaluationSummary || '',
      commentsList,
    },
    lastAttempt: null,
    status: 'pending',
  };

  saveSyncQueue(queue);
  return true;
}

/**
 * Update a sync queue item.
 * @param {string} itemId
 * @param {Object} updates
 */
export function updateSyncQueueItem(itemId, updates) {
  const queue = getSyncQueue();
  const idx = queue.findIndex((item) => item.id === itemId);
  if (idx >= 0) {
    queue[idx] = { ...queue[idx], ...updates };
    saveSyncQueue(queue);
  }
}

/**
 * Remove a sync queue item after successful sync.
 * @param {string} itemId
 */
export function removeSyncQueueItem(itemId) {
  const queue = getSyncQueue().filter((item) => item.id !== itemId);
  saveSyncQueue(queue);
}

/**
 * Remove every sync queue item associated with a given draft key.
 * Used to clean up queued offline work once the same evaluation has been
 * successfully saved/submitted while online.
 * @param {string} draftKey
 * @returns {number} Number of queue items removed
 */
export function removeSyncQueueItemsByDraftKey(draftKey) {
  if (!draftKey) {
    return 0;
  }
  const queue = getSyncQueue();
  const remaining = queue.filter((item) => item.draftKey !== draftKey);
  const removed = queue.length - remaining.length;
  if (removed > 0) {
    saveSyncQueue(remaining);
  }
  return removed;
}

/**
 * Get count of pending sync items.
 * @returns {number}
 */
export function getPendingSyncCount() {
  return getSyncQueue().filter((item) => item.status === 'pending' || item.status === 'failed').length;
}

// ─── localStorage: Reference Data Cache ─────────────────────────────

/**
 * Cache reference data (categories, competencies, events, tangibles).
 * @param {string} dataType - e.g. 'categories', 'competencies', 'events', 'tangibles'
 * @param {*} data
 */
export function cacheReferenceData(dataType, data) {
  try {
    const cacheItem = {
      data,
      cachedAt: Date.now(),
    };
    localStorage.setItem(`${REF_DATA_KEY_PREFIX}${dataType}`, JSON.stringify(cacheItem));
  } catch (e) {
    console.warn(`Failed to cache ${dataType}:`, e);
  }
}

/**
 * Get cached reference data.
 * @param {string} dataType
 * @param {number} maxAgeMs - Maximum age in ms before considered stale (default: 24 hours)
 * @returns {*|null}
 */
export function getCachedReferenceData(dataType, maxAgeMs = 86400000) {
  try {
    const raw = localStorage.getItem(`${REF_DATA_KEY_PREFIX}${dataType}`);
    if (!raw) return null;
    const cacheItem = JSON.parse(raw);
    if (Date.now() - cacheItem.cachedAt > maxAgeMs) return null;
    return cacheItem.data;
  } catch {
    return null;
  }
}
